import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { Toaster } from './components/ui/sonner';
import BackendHealthBanner from './components/BackendHealthBanner';
import ActivationPage from './pages/public/ActivationPage';
import ActivationFormPage from './pages/public/ActivationFormPage';
import ActivationThankYouPage from './pages/public/ActivationThankYouPage';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { ClientsPage } from './pages/ClientsPage';
import ClientDetailPage from './pages/ClientDetailPage';
import { ProjectsPage } from './pages/ProjectsPage';
import { TasksPage } from './pages/TasksPage';
import NotificationsPage from './pages/NotificationsPage';
import { InvoicesPage } from './pages/InvoicesPage';
import { RaiseInvoicePage } from './pages/RaiseInvoicePage';
import { EmployeesPage } from './pages/EmployeesPage';
import { DomainsPage } from './pages/DomainsPage';
import { HostingPage } from './pages/HostingPage';
import { SSLPage } from './pages/SSLPage';
import { CredentialsPage } from './pages/CredentialsPage';
import { LeavesPage } from './pages/LeavesPage';
import { AttendancePage } from './pages/AttendancePage';
import { PayrollPage } from './pages/PayrollPage';
import { ProfessionalTaxReportPage } from './pages/ProfessionalTaxReportPage';
import { LeadsPage } from './pages/LeadsPage';
import { TicketsPage } from './pages/TicketsPage';
import { AMCPage } from './pages/AMCPage';
import { DLTPage } from './pages/DLTPage';
import { UsersPage } from './pages/UsersPage';
import { RolesPage } from './pages/RolesPage';
import { ProfilePage } from './pages/ProfilePage';
import { SettingsPage } from './pages/SettingsPage';
import { ActivityLogPage } from './pages/ActivityLogPage';
import { HolidaysPage } from './pages/HolidaysPage';
import { EmployeeDetailPage } from './pages/EmployeeDetailPage';
import { TransactionsPage } from './pages/TransactionsPage';
import { StatementPage } from './pages/StatementPage';
import { PaymentLinksPage } from './pages/PaymentLinksPage';
import { EmailSettingsPage } from './pages/EmailSettingsPage';
import { MyPortalPage } from './pages/portal/MyPortalPage';
import { MyAttendancePage } from './pages/portal/MyAttendancePage';
import { MyPayslipsPage } from './pages/portal/MyPayslipsPage';
import { MyLeavePage } from './pages/portal/MyLeavePage';
import { MyProfilePage } from './pages/portal/MyProfilePage';
import { MySelfReviewPage } from './pages/portal/MySelfReviewPage';
import { SMBLeadList } from './pages/smb/SMBLeadList';
import { SMBLeadDetail } from './pages/smb/SMBLeadDetail';
import { SMBActivationPipeline } from './pages/smb/SMBActivationPipeline';
// Pipeline temporarily disabled — kept as commented import in case it's re-enabled.
// import { SMBPipeline } from './pages/smb/SMBPipeline';
import { SMBFollowups } from './pages/smb/SMBFollowups';
import { SMBDashboard } from './pages/smb/SMBDashboard';
import { SMBAssignmentRules } from './pages/smb/SMBAssignmentRules';
import SMBOutreachTemplates from './pages/smb/SMBOutreachTemplates';
import SMBInboxPage from './pages/smb/SMBInboxPage';
import { BankAccountsPage } from './pages/BankAccountsPage';
import { GSTRReportPage } from './pages/GSTRReportPage';
import { AppraisalPage } from './pages/AppraisalPage';
import { EODNotesPage } from './pages/EODNotesPage';
import { ProjectDetailPage } from './pages/ProjectDetailPage';
import { TodoPage } from './pages/TodoPage';
import { RenewalAlertsPage } from './pages/RenewalAlertsPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import MigrationPage from './pages/MigrationPage';
import SystemFlagsPage from './pages/SystemFlagsPage';
import { DashboardLayout } from './components/layout/DashboardLayout';
import { Loader2 } from 'lucide-react';
import { ConfirmationProvider } from './components/ConfirmationProvider';
import { PageSizeProvider } from './context/PageSizeContext';
import { FeatureFlagsProvider, useFeatureFlags } from './context/FeatureFlagsContext';
import './index.css';

// Protected Route Component
const ProtectedRoute = ({ children, permission, anyPermission, dangerFlag }) => {
  const { isAuthenticated, loading, hasPermission, user } = useAuth();
  const { dangerToolsEnabled, loading: flagsLoading } = useFeatureFlags();

  if (loading || (dangerFlag && flagsLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Feature-flag guard: if the route is gated behind the danger-tools flag
  // and the flag is off, redirect to dashboard regardless of permissions.
  if (dangerFlag && !dangerToolsEnabled) {
    const fallback = user?.is_employee ? '/my-portal' : '/dashboard';
    return <Navigate to={fallback} replace />;
  }

  // Permission check: if a permission is required and user doesn't have it, redirect
  if (permission && !hasPermission(permission)) {
    const fallback = user?.is_employee ? '/my-portal' : '/dashboard';
    return <Navigate to={`${fallback}?access_denied=1`} replace />;
  }
  // ANY-of: route accessible if the user has at least one of the listed keys
  if (anyPermission && !anyPermission.some(p => hasPermission(p))) {
    const fallback = user?.is_employee ? '/my-portal' : '/dashboard';
    return <Navigate to={`${fallback}?access_denied=1`} replace />;
  }

  return <DashboardLayout>{children}</DashboardLayout>;
};

// Public Route (redirect to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Placeholder Pages for modules not yet implemented
const PlaceholderPage = ({ title }) => (
  <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
    <div className="p-8 rounded-lg bg-card border border-border max-w-md">
      <h2 className="text-2xl font-bold mb-2">{title}</h2>
      <p className="text-muted-foreground">
        This module is coming soon. Connect to your PHP server to enable this feature.
      </p>
    </div>
  </div>
);

function App() {
  return (
    <ThemeProvider>
    <ConfirmationProvider>
    <AuthProvider>
    <FeatureFlagsProvider>
    <PageSizeProvider>
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route 
            path="/login" 
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            } 
          />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          {/* SMB Activation public flow — token-gated, no auth */}
          <Route path="/activate/:token" element={<ActivationPage />} />
          <Route path="/activate/:token/form" element={<ActivationFormPage />} />
          <Route path="/activate/:token/thank-you" element={<ActivationThankYouPage />} />

          {/* Protected Routes */}
          <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/clients" element={<ProtectedRoute permission="clients.view"><ClientsPage /></ProtectedRoute>} />
          <Route path="/clients/:id" element={<ProtectedRoute permission="clients.view"><ClientDetailPage /></ProtectedRoute>} />
          <Route path="/projects" element={<ProtectedRoute permission="projects.view"><ProjectsPage /></ProtectedRoute>} />
          <Route path="/projects/:id" element={<ProtectedRoute permission="projects.view"><ProjectDetailPage /></ProtectedRoute>} />
          <Route path="/tasks" element={<ProtectedRoute permission="tasks.view"><TasksPage /></ProtectedRoute>} />
          <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
          <Route path="/leads" element={<ProtectedRoute permission="leads.view"><LeadsPage /></ProtectedRoute>} />
          <Route path="/tickets" element={<ProtectedRoute permission="tickets.view"><TicketsPage /></ProtectedRoute>} />
          <Route path="/domains" element={<ProtectedRoute permission="domains.view"><DomainsPage /></ProtectedRoute>} />
          <Route path="/hosting" element={<ProtectedRoute permission="hosting.view"><HostingPage /></ProtectedRoute>} />
          <Route path="/ssl" element={<ProtectedRoute permission="domains.view"><SSLPage /></ProtectedRoute>} />
          <Route path="/credentials" element={<ProtectedRoute permission="credentials.view"><CredentialsPage /></ProtectedRoute>} />
          <Route path="/dlt" element={<ProtectedRoute permission="domains.view"><DLTPage /></ProtectedRoute>} />
          <Route path="/invoices/new" element={<ProtectedRoute permission="invoices.create"><RaiseInvoicePage /></ProtectedRoute>} />
          <Route path="/invoices" element={<ProtectedRoute permission="invoices.view"><InvoicesPage /></ProtectedRoute>} />
          <Route path="/transactions" element={<ProtectedRoute permission="invoices.view"><TransactionsPage /></ProtectedRoute>} />
          <Route path="/statement" element={<ProtectedRoute permission="invoices.view"><StatementPage /></ProtectedRoute>} />
          <Route path="/amc" element={<ProtectedRoute permission="invoices.view"><AMCPage /></ProtectedRoute>} />
          <Route path="/employees" element={<ProtectedRoute permission="employees.view"><EmployeesPage /></ProtectedRoute>} />
          <Route path="/employees/:id" element={<ProtectedRoute permission="employees.view"><EmployeeDetailPage /></ProtectedRoute>} />
          <Route path="/attendance" element={<ProtectedRoute permission="employees.view"><AttendancePage /></ProtectedRoute>} />
          <Route path="/leaves" element={<ProtectedRoute permission="leaves.view"><LeavesPage /></ProtectedRoute>} />
          <Route path="/payroll" element={<ProtectedRoute permission="employees.view"><PayrollPage /></ProtectedRoute>} />
          <Route path="/payroll/professional-tax" element={<ProtectedRoute permission="employees.view"><ProfessionalTaxReportPage /></ProtectedRoute>} />
          <Route path="/appraisals" element={<ProtectedRoute permission="employees.view"><AppraisalPage /></ProtectedRoute>} />
          <Route path="/users" element={<ProtectedRoute permission="users.view"><UsersPage /></ProtectedRoute>} />
          <Route path="/roles" element={<ProtectedRoute permission="roles.view"><RolesPage /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute permission="settings.view"><SettingsPage /></ProtectedRoute>} />
          <Route path="/settings/email" element={<ProtectedRoute permission="settings.view"><EmailSettingsPage /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          <Route path="/activity-log" element={<ProtectedRoute permission="settings.view"><ActivityLogPage /></ProtectedRoute>} />
          <Route path="/holidays" element={<ProtectedRoute permission="employees.view"><HolidaysPage /></ProtectedRoute>} />

          {/* Self-Service Portal */}
          <Route path="/my-portal" element={<ProtectedRoute><MyPortalPage /></ProtectedRoute>} />
          <Route path="/my-portal/attendance" element={<ProtectedRoute><MyAttendancePage /></ProtectedRoute>} />
          <Route path="/my-portal/payslips" element={<ProtectedRoute><MyPayslipsPage /></ProtectedRoute>} />
          <Route path="/my-portal/leave" element={<ProtectedRoute><MyLeavePage /></ProtectedRoute>} />
          <Route path="/my-portal/profile" element={<ProtectedRoute><MyProfilePage /></ProtectedRoute>} />
          <Route path="/my-portal/self-review" element={<ProtectedRoute><MySelfReviewPage /></ProtectedRoute>} />

          {/* Myzow SMB */}
          <Route path="/smb/leads" element={<ProtectedRoute permission="smb.view"><SMBLeadList /></ProtectedRoute>} />
          <Route path="/smb/leads/:id" element={<ProtectedRoute permission="smb.view"><SMBLeadDetail /></ProtectedRoute>} />
          {/* Sales Pipeline temporarily disabled (June 2026). The
              component import + this route are kept so the page can be
              re-enabled by switching the element back to <SMBPipeline />.
              Direct navigation or bookmarks now redirect to /smb/leads. */}
          <Route path="/smb/pipeline" element={<Navigate to="/smb/leads" replace />} />
          <Route path="/smb/activation-pipeline" element={<ProtectedRoute permission="smb.view"><SMBActivationPipeline /></ProtectedRoute>} />
          <Route path="/smb/followups" element={<ProtectedRoute permission="smb.view"><SMBFollowups /></ProtectedRoute>} />
          <Route path="/smb/dashboard" element={<ProtectedRoute permission="smb.performance"><SMBDashboard /></ProtectedRoute>} />
          <Route path="/smb/assignment-rules" element={<ProtectedRoute permission="smb.assignment_rules"><SMBAssignmentRules /></ProtectedRoute>} />
          <Route path="/smb/outreach-templates" element={<ProtectedRoute permission="smb.outreach"><SMBOutreachTemplates /></ProtectedRoute>} />
          <Route path="/smb/inbox" element={<ProtectedRoute anyPermission={['whatsapp.inbox.smb', 'whatsapp.inbox.general', 'smb.inbox']}><SMBInboxPage /></ProtectedRoute>} />

          {/* Finance */}
          <Route path="/bank-accounts" element={<ProtectedRoute permission="invoices.view"><BankAccountsPage /></ProtectedRoute>} />
          <Route path="/gstr-report" element={<ProtectedRoute permission="invoices.view"><GSTRReportPage /></ProtectedRoute>} />
          <Route path="/finance/payment-links" element={<ProtectedRoute permission="payment_links.view"><PaymentLinksPage /></ProtectedRoute>} />

          {/* Settings */}
          <Route path="/settings/renewals" element={<ProtectedRoute permission="settings.view"><RenewalAlertsPage /></ProtectedRoute>} />
          <Route path="/settings/system-flags" element={<ProtectedRoute permission="settings.view"><SystemFlagsPage /></ProtectedRoute>} />
          <Route path="/settings/migration" element={<ProtectedRoute permission="migration.run" dangerFlag><MigrationPage /></ProtectedRoute>} />
          <Route path="/eod-notes" element={<ProtectedRoute><EODNotesPage /></ProtectedRoute>} />
          <Route path="/todo" element={<ProtectedRoute><TodoPage /></ProtectedRoute>} />

          {/* Default Redirects */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
      <BackendHealthBanner />
    </PageSizeProvider>
    </FeatureFlagsProvider>
    </AuthProvider>
    </ConfirmationProvider>
    </ThemeProvider>
  );
}

export default App;
